import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Card, Button, TextArea, Note, Quiet, NotRunning, C, when, dueWhen, errText } from "./ui.jsx";
import { Transcript } from "./Applicant.jsx";
import CallDriver from "./CallDriver.jsx";
import { laneLine, scheduleFor, callsOn, outcomeWords, REASON_LABEL } from "./dispatchView.js";

/**
 * One load (the schematic's "Dispatch · one load" board): the check calls it gets, what the calls
 * came to, a call now, and the team's own notes.
 *
 * ⛔ TEAM NOTES NEVER REACH A DRIVER. They are stored apart from the load (backend load_notes) and
 * nothing sends them to the voice platform. What Alfred tells a driver is typed into the call
 * itself, for that call only.
 */

const STATE = { done: "Done", next: "Next", later: "Later" };

const SCHED_STATE = { scheduled: null, placing: "Calling now", placed: "Placed", refused: "Did not go out", cancelled: "Cancelled" };
// ⛔ Cut off while being placed, or no answer from the platform in time: it may have gone (desk.mjs).
const MAY_HAVE_GONE = new Set(["interrupted", "failed_may_have_gone", "platform_timeout"]);
const schedState = (s) => (s.status === "refused" && MAY_HAVE_GONE.has(s.error)
  ? "May have gone out. Check the driver's calls before calling again."
  : SCHED_STATE[s.status] && `${SCHED_STATE[s.status]}${s.error ? ` (${String(s.error).replace(/_/g, " ")})` : ""}`);

/** Calls set for later (Call later): these DO go out on their own, so they sit apart from the check calls. */
export function ScheduledCalls({ scheduled = [], onCancel, busy, now = new Date() }) {
  return (
    <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
      {scheduled.map((s) => (
        <li key={`s${s.id}`} style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 16, alignItems: "baseline" }}>
          <span style={{ fontWeight: 600, minWidth: 150 }}>{dueWhen(s.dueAt, now)}</span>
          <span style={{ flex: "1 1 300px" }}>
            {`Set by ${s.requestedBy}${s.message ? `: ${s.message}` : "."}`}
            {schedState(s) && <span style={{ display: "block", fontSize: 14, color: s.status === "refused" && !MAY_HAVE_GONE.has(s.error) ? C.bad : C.muted }}>{schedState(s)}</span>}
          </span>
          {s.status === "scheduled" && onCancel && (
            <Button kind="quiet" disabled={busy} onClick={() => onCancel(s.id)} label={`Cancel the call set for ${dueWhen(s.dueAt, now)}`} style={{ padding: 0, minHeight: 32 }}>Cancel</Button>
          )}
        </li>
      ))}
    </ol>
  );
}

export function Schedule({ steps }) {
  return (
    <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
      {steps.map((s) => (
        <li key={s.key} style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 16 }}>
          <span style={{ fontWeight: 600, minWidth: 96, color: s.state === "next" ? C.accent : s.state === "later" ? C.muted : C.ink }}>{STATE[s.state]}</span>
          <span style={{ flex: "1 1 360px", color: s.state === "next" ? C.accent : s.state === "later" ? C.muted : C.ink, fontWeight: s.state === "next" ? 600 : 400 }}>
            {s.label}{s.call ? `. ${REASON_LABEL[s.call.reason]} at ${when(s.call.startedAt || s.call.createdAt)}: talked.` : "."}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function CallsSaid({ calls, open, setOpen }) {
  if (!calls.length) return <Quiet>No calls on this load yet.</Quiet>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {calls.map((c) => (
        <div key={c.id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "baseline" }}>
            <span style={{ fontSize: 15, color: C.muted, minWidth: 96 }}>{when(c.startedAt || c.createdAt)}</span>
            <span style={{ fontSize: 16, flex: "1 1 300px" }}>
              {c.seeded ? `${REASON_LABEL[c.reason] || "Call"}: seeded for a demonstration. This call was not placed.`
                : `${REASON_LABEL[c.reason] || "Call"}: ${outcomeWords(c)}.${c.persona ? " A demonstration: an AI played the driver." : ""}`}
            </span>
            {String(c.transcript || "").trim() && (
              <Button kind="quiet" onClick={() => setOpen(open === c.id ? null : c.id)} style={{ padding: 0, minHeight: 32 }} expanded={open === c.id}>
                {open === c.id ? "Hide" : "Read what was said"}
              </Button>
            )}
          </div>
          {open === c.id && c.seeded && <Quiet>Written for a demonstration. Nobody said this on a call.</Quiet>}
          {open === c.id && <Transcript text={c.transcript} />}
        </div>
      ))}
    </div>
  );
}

export function TeamNotes({ notes, draft, setDraft, onSave, busy }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {notes.length === 0 && <Quiet>No notes yet.</Quiet>}
      {notes.map((n) => (
        <div key={n.id} style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 16 }}>
          <span style={{ color: C.muted, minWidth: 96, fontSize: 15 }}>{when(n.at)}</span>
          <span style={{ flex: "1 1 360px", whiteSpace: "pre-wrap" }}>{`${n.name || n.by}: ${n.body}`}</span>
        </div>
      ))}
      <TextArea id="team-note" label="Add a note" value={draft} onChange={setDraft} rows={2} maxLength={1000} />
      <div><Button kind="secondary" disabled={busy || !draft.trim()} onClick={onSave}>Save note</Button></div>
    </div>
  );
}

export function LoadView({ company, stage, data, error, open, setOpen, draft, setDraft, busy, msg, onSaveNote, onCalled, onCancel }) {
  if (error) return <Shell company={company} stage={stage}><Back to="/carrier/dispatch">Dispatch</Back><Quiet>{error}</Quiet></Shell>;
  if (!data) return <Shell company={company} stage={stage}><Back to="/carrier/dispatch">Dispatch</Back><Quiet>Loading…</Quiet></Shell>;
  const { load, driver, calls = [], notes = [], checkCalls } = data;
  const steps = scheduleFor(load, calls, checkCalls);
  const said = callsOn(load, calls);
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/dispatch">Dispatch</Back>
      <Title sub={laneLine(load)}>{[load.ref, driver?.name].filter(Boolean).join(" · ")}</Title>
      {driver && <Link to={`/carrier/dispatch/drivers/${driver.id}`} style={{ fontSize: 15, fontWeight: 500, textDecoration: "none" }}>{`${driver.name}'s calls and details →`}</Link>}

      {(data.scheduled || []).length > 0 && (
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Set for later</h2>
          <ScheduledCalls scheduled={data.scheduled} onCancel={onCancel} busy={busy} />
        </Card>
      )}

      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Check calls</h2>
          {/* ⛔ Alfred does not place these on its own yet (the dispatch worker, plan "Not in this build"): they are the plan a dispatcher calls through. */}
          <NotRunning>Not placed automatically yet</NotRunning>
        </div>
        <Schedule steps={steps} />
      </Card>

      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>What the calls said</h2>
        <CallsSaid calls={said} open={open} setOpen={setOpen} />
      </Card>

      <CallDriver driver={driver} load={load} onDone={onCalled} />

      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Team notes</h2>
          <p style={{ margin: 0, fontSize: 15, color: C.muted }}>Only your team sees these. Alfred never reads them to a driver.</p>
        </div>
        <TeamNotes notes={notes} draft={draft} setDraft={setDraft} onSave={onSaveNote} busy={busy} />
        <Note msg={msg} />
      </Card>
    </Shell>
  );
}

export default function LoadPage() {
  const { carrier } = useCarrier();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // ⛔ ONLY THE NEWEST ANSWER IS SHOWN (review): moving between loads, a slow answer for the last one
  // must not land on this one.
  const seq = useRef(0);
  const load = useCallback(() => {
    const n = ++seq.current;
    return freight.dispatchLoad(id).then((d) => { if (n === seq.current) setData(d); })
      .catch((e) => { if (n === seq.current) setError(e?.status === 404 ? "No such load, or it is not one of yours." : errText(e)); });
  }, [id]);
  useEffect(() => { setData(null); setError(null); load(); }, [load]);
  const onSaveNote = async () => {
    setBusy(true); setMsg(null);
    try { const notes = await freight.addLoadNote(id, draft.trim()); setData((d) => ({ ...d, notes })); setDraft(""); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  const onCancel = async (sid) => {
    setBusy(true); setMsg(null);
    try { await freight.cancelScheduled(sid); await load(); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return <LoadView company={carrier.name} stage={carrier.stage} data={data} error={error} open={open} setOpen={setOpen}
    draft={draft} setDraft={setDraft} busy={busy} msg={msg} onSaveNote={onSaveNote} onCalled={() => load()} onCancel={onCancel} />;
}
